// eslint-disable-next-line no-restricted-imports
import { reloadAgentProfile } from "@src/app/store/session/actions";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { REFERRAL_REDEMPTION_STORAGE_KEY } from "@src/appV2/Referral/RedeemCode/constants";
import { type ReferralRedemption } from "@src/appV2/Referral/RedeemCode/types";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
// eslint-disable-next-line no-restricted-imports
import { useDispatch } from "react-redux";
import { z } from "zod";

import { ONBOARDING_STAGES } from "../util/constants";
import { EmailAlreadyInUseError } from "./errors/emailAlreadyInUse.error";
import { InvalidEmailError } from "./errors/invalidEmail.error";
import {
  type UpdateOnboardingWorkerResponse,
  useUpdateOnboardingWorker,
} from "./useUpdateOnboardingWorker";

export const VERIFY_EMAIL_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/onboarding/validateEmail`;

export async function verifyOnboardingEmail(email: string): Promise<boolean> {
  const emailSchema = z.string().email();
  const validatedEmail = emailSchema.safeParse(email);
  if (!validatedEmail.success) {
    logError(APP_V2_APP_EVENTS.ONBOARDING_UPDATE_EMAIL_ERROR, {
      error: new InvalidEmailError(),
      metadata: {
        email,
        errorCode: "InvalidEmailError",
      },
    });
    return false;
  }

  // Validates if the email domain is valid
  const response = await get({
    url: VERIFY_EMAIL_URL,
    queryParams: { email },
    responseSchema: z.object({
      isEmailDomainValid: z.boolean(),
    }),
    requestSchema: z.object({
      email: emailSchema,
    }),
  });

  return Boolean(response.data.isEmailDomainValid);
}

function extractUpdateOnboardingEmailErrorMessage(error: AxiosError): string {
  const data: unknown = error.response?.data;

  // Check if data is a string
  if (typeof data === "string") {
    return data;
  }

  return "";
}

export function useUpdateOnboardingEmail() {
  const dispatch = useDispatch();
  const { mutateAsync: updateOnboardingWorker } = useUpdateOnboardingWorker();
  const [referralRedemption] = useLocalStorage<ReferralRedemption | undefined>(
    REFERRAL_REDEMPTION_STORAGE_KEY,
    undefined
  );
  return useMutation({
    mutationKey: ["updateOnboardingEmail"],
    mutationFn: async (props: { newEmail: string }) => {
      const { newEmail } = props;
      const isValid = await verifyOnboardingEmail(newEmail);
      if (!isValid) {
        throw new InvalidEmailError();
      }

      let response: UpdateOnboardingWorkerResponse;
      try {
        response = await updateOnboardingWorker({
          stage: ONBOARDING_STAGES.EMAIL,
          signupFlowVersion: "V2",
          email: newEmail,
        });
      } catch (unknownError: unknown) {
        if (unknownError instanceof AxiosError) {
          const errorText = extractUpdateOnboardingEmailErrorMessage(unknownError);
          if (errorText.toLowerCase().includes("email address already in use")) {
            throw new EmailAlreadyInUseError();
          }
        }

        throw unknownError;
      }

      logEvent(APP_V2_USER_EVENTS.ONBOARDING_STEP_COMPLETED, {
        stepCompleted: ONBOARDING_STAGES.EMAIL,
        workerId: response.agent?.userId,
        email: newEmail,
        referralCode: referralRedemption?.code,
      });

      dispatch(reloadAgentProfile());
    },
  });
}
